import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { SocFormData } from 'components/Assessments/SocAssessment/types/SocFormData'
import { SocFormField } from 'components/Assessments/SocAssessment/types/SocFormField'
import { SocAssessmentQuestionnaireItem } from 'components/Assessments/SocAssessment/types/questionnaire'

/**
 * Risk Assessment subsection adapter
 * Handles complex risk assessments including ScoredChecklist components (18 fields)
 * This is one of the more complex subsections with various risk factors and safety measures
 */
export class RiskAssessmentAdapter extends BaseSubsectionAdapter<SocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<SocFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Hospitalization risk assessment
    const hospitalizationRisk = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOSPITALIZATION_RISK
    )?.at(0)

    const hospitalizationRiskFactors = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS
    )

    const hospitalizationRiskFactorsOther = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS_OTHER
    )?.at(0)

    // Emergency planning
    const triageCode = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_TRIAGE_CODE
    )?.at(0)

    const emergencyPlan = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_EMERGENCY_PLAN
    )?.at(0)

    const safeRoom = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_SAFE_ROOM
    )

    // Fire safety assessment
    const fireAssessmentOxygen = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FIRE_ASSESSMENT_OXYGEN
    )?.at(0)

    const fireSafetyPrecautions = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FIRE_SAFETY_PRECAUTIONS
    )

    // Homebound status
    const homeboundStatus = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOMEBOUND_STATUS
    )?.at(0)

    // Infection risk assessment
    const infectionRisk = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_INFECTION_RISK
    )?.at(0)

    const infectionRiskFactors = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_INFECTION_RISK_FACTORS
    )

    const infectionRiskFactorsOther = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_INFECTION_RISK_FACTORS_OTHER
    )?.at(0)

    // Fall risk assessment (ScoredChecklist component)
    const fallRisk = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK
    )?.at(0)

    const fallRiskFactors = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK_FACTORS
    )

    const fallRiskFactorsOther = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK_FACTORS_OTHER
    )?.at(0)

    const fallRiskAssessment = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK_ASSESSMENT
    )

    // Financial and environmental safety
    const financeRisk = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FINANCE_RISK
    )?.at(0)

    const financeRiskOther = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FINANCE_RISK_OTHER
    )?.at(0)

    const environmentSafety = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_ENVIRONMENT_SAFETY
    )

    const nurseSafetyRisks = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_NURSE_SAFETY_RISKS
    )

    const safetyMeasures = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_SAFETY_MEASURES
    )

    const notes = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_NOTES
    )?.at(0)

    return {
      [SocFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK]: hospitalizationRisk,
      [SocFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS]: hospitalizationRiskFactors,
      [SocFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS_OTHER]:
        hospitalizationRiskFactorsOther,
      [SocFormField.RISK_ASSESSMENT_TRIAGE_CODE]: triageCode,
      [SocFormField.RISK_ASSESSMENT_EMERGENCY_PLAN]: emergencyPlan,
      [SocFormField.RISK_ASSESSMENT_SAFE_ROOM]: safeRoom,
      [SocFormField.RISK_ASSESSMENT_FIRE_ASSESSMENT_OXYGEN]: fireAssessmentOxygen,
      [SocFormField.RISK_ASSESSMENT_FIRE_SAFETY_PRECAUTIONS]: fireSafetyPrecautions,
      [SocFormField.RISK_ASSESSMENT_HOMEBOUND_STATUS]: homeboundStatus,
      [SocFormField.RISK_ASSESSMENT_INFECTION_RISK]: infectionRisk,
      [SocFormField.RISK_ASSESSMENT_INFECTION_RISK_FACTORS]: infectionRiskFactors,
      [SocFormField.RISK_ASSESSMENT_INFECTION_RISK_FACTORS_OTHER]: infectionRiskFactorsOther,
      [SocFormField.RISK_ASSESSMENT_FALL_RISK]: fallRisk,
      [SocFormField.RISK_ASSESSMENT_FALL_RISK_FACTORS]: fallRiskFactors,
      [SocFormField.RISK_ASSESSMENT_FALL_RISK_FACTORS_OTHER]: fallRiskFactorsOther,
      [SocFormField.RISK_ASSESSMENT_FALL_RISK_ASSESSMENT]: fallRiskAssessment,
      [SocFormField.RISK_ASSESSMENT_FINANCE_RISK]: financeRisk,
      [SocFormField.RISK_ASSESSMENT_FINANCE_RISK_OTHER]: financeRiskOther,
      [SocFormField.RISK_ASSESSMENT_ENVIRONMENT_SAFETY]: environmentSafety,
      [SocFormField.RISK_ASSESSMENT_NURSE_SAFETY_RISKS]: nurseSafetyRisks,
      [SocFormField.RISK_ASSESSMENT_SAFETY_MEASURES]: safetyMeasures,
      [SocFormField.RISK_ASSESSMENT_NOTES]: notes
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = [
      {
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOSPITALIZATION_RISK,
        value: this.getFormDataField(SocFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK)
      },
      {
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS,
        value: this.getFormDataField(SocFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS)
      }
    ]

    if (this.formDataFieldExists(SocFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS_OTHER)) {
      mutations.push({
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS_OTHER,
        value: this.getFormDataField(
          SocFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS_OTHER
        )
      })
    }
    mutations.push(
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_TRIAGE_CODE,
        value: this.getFormDataField(SocFormField.RISK_ASSESSMENT_TRIAGE_CODE)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_EMERGENCY_PLAN,
        value: this.getFormDataField(SocFormField.RISK_ASSESSMENT_EMERGENCY_PLAN)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_SAFE_ROOM,
        value: this.getFormDataField(SocFormField.RISK_ASSESSMENT_SAFE_ROOM)
      },
      {
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FIRE_ASSESSMENT_OXYGEN,
        value: this.getFormDataField(SocFormField.RISK_ASSESSMENT_FIRE_ASSESSMENT_OXYGEN)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOMEBOUND_STATUS,
        value: this.getFormDataField(SocFormField.RISK_ASSESSMENT_HOMEBOUND_STATUS)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_INFECTION_RISK,
        value: this.getFormDataField(SocFormField.RISK_ASSESSMENT_INFECTION_RISK)
      },
      {
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_INFECTION_RISK_FACTORS,
        value: this.getFormDataField(SocFormField.RISK_ASSESSMENT_INFECTION_RISK_FACTORS)
      }
    )
    if (this.formDataFieldExists(SocFormField.RISK_ASSESSMENT_INFECTION_RISK_FACTORS_OTHER)) {
      mutations.push({
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_INFECTION_RISK_FACTORS_OTHER,
        value: this.getFormDataField(SocFormField.RISK_ASSESSMENT_INFECTION_RISK_FACTORS_OTHER)
      })
    }
    mutations.push(
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK,
        value: this.getFormDataField(SocFormField.RISK_ASSESSMENT_FALL_RISK)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK_FACTORS,
        value: this.getFormDataField(SocFormField.RISK_ASSESSMENT_FALL_RISK_FACTORS)
      }
    )
    if (this.formDataFieldExists(SocFormField.RISK_ASSESSMENT_FALL_RISK_FACTORS_OTHER)) {
      mutations.push({
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK_FACTORS_OTHER,
        value: this.getFormDataField(SocFormField.RISK_ASSESSMENT_FALL_RISK_FACTORS_OTHER)
      })
    }
    mutations.push(
      {
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK_ASSESSMENT,
        value: this.getFormDataField(SocFormField.RISK_ASSESSMENT_FALL_RISK_ASSESSMENT)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FINANCE_RISK,
        value: this.getFormDataField(SocFormField.RISK_ASSESSMENT_FINANCE_RISK)
      }
    )
    if (this.formDataFieldExists(SocFormField.RISK_ASSESSMENT_FINANCE_RISK_OTHER)) {
      mutations.push({
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FINANCE_RISK_OTHER,
        value: this.getFormDataField(SocFormField.RISK_ASSESSMENT_FINANCE_RISK_OTHER)
      })
    }
    mutations.push(
      {
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_ENVIRONMENT_SAFETY,
        value: this.getFormDataField(SocFormField.RISK_ASSESSMENT_ENVIRONMENT_SAFETY)
      },
      {
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_NURSE_SAFETY_RISKS,
        value: this.getFormDataField(SocFormField.RISK_ASSESSMENT_NURSE_SAFETY_RISKS)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_SAFETY_MEASURES,
        value: this.getFormDataField(SocFormField.RISK_ASSESSMENT_SAFETY_MEASURES)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_NOTES,
        value: this.getFormDataField(SocFormField.RISK_ASSESSMENT_NOTES)
      }
    )

    if (this.formDataFieldExists(SocFormField.RISK_ASSESSMENT_FIRE_SAFETY_PRECAUTIONS)) {
      mutations.push({
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FIRE_SAFETY_PRECAUTIONS,
        value: this.getFormDataField(SocFormField.RISK_ASSESSMENT_FIRE_SAFETY_PRECAUTIONS)
      })
    }

    return mutations
  }
}
